import { collapseDrawerState, Divider, Drawer as DrawerUI, Grid, Spacing } from '@approvalmax/ui/src/components';
import { useAtomValue } from 'jotai';
import { selectors } from 'modules/common';
import { domain } from 'modules/data';
import { useSelector } from 'modules/react-redux';
import { memo, useMemo, useRef } from 'react';
import {
    DomainEntitiesCompanyRole,
    useGetCompanyMe,
    useGetCompanyWorkflows,
    useGetInvitations,
} from 'shared/data/webApp/v2';
import { isPayOrCapturePromotionsAllowedV2 } from 'shared/helpers';
import { useAddonPromotions } from 'shared/hooks/useAddonPromotions';
import { usePermissionsV2 } from 'shared/hooks/usePermissions';
import { starterGuideCompaniesState } from 'shared/states';
import { AddonPromotionType, pageVisiblePromotionsState } from 'shared/states';

import {
    AllCompaniesMenu,
    AllRequestsMenu,
    BudgetingMenu,
    CompanyAlerts,
    CompanySelector,
    FeedbackPromotion,
    Invitations,
    NavigationPromotion,
    PayMenu,
    ReportsMenu,
    SettingsMenu,
    WorkflowsMenu,
} from './components';
import { DrawerPayAndCapturePromotion } from './components/DrawerPayAndCapturePromotion/DrawerPayAndCapturePromotion';
import { GettingStartedMenu } from './components/GettingStartedMenu/GettingStartedMenu';
import { useCompanies, useCompany, useHandleMenuClick } from './Drawer.hooks';
import { Loading } from './Drawer.loading';

const Drawer = memo(() => {
    const contentRef = useRef<HTMLDivElement>(null);
    const isDrawerCollapsed = useAtomValue(collapseDrawerState);
    const pageVisiblePromotions = useAtomValue(pageVisiblePromotionsState);

    const { data: invitations } = useGetInvitations();
    const profile = useSelector(selectors.profile.findProfile);
    const { data: company } = useCompany();
    const { data: me } = useGetCompanyMe({ path: { companyId: company?.id } });
    const { data: workflows } = useGetCompanyWorkflows({ path: { companyId: company?.id } });
    const companies = useCompanies();
    const { canShowPromotion } = useAddonPromotions();

    const { isPaymentDetailsViewEnabled } = usePermissionsV2(company?.id);

    const isAuditor = me?.role === DomainEntitiesCompanyRole.Auditor;
    const isManager = me?.role === DomainEntitiesCompanyRole.Manager;
    const isAuditorOrManager = isAuditor || isManager;
    const isWorkflowManager = me?.role === DomainEntitiesCompanyRole.WorkflowManager;

    const isSimpleUser = !isAuditorOrManager && !isWorkflowManager;

    const showPromotions = !isDrawerCollapsed && !pageVisiblePromotions;

    const showPayPromotion =
        showPromotions &&
        company &&
        isPayOrCapturePromotionsAllowedV2(company, AddonPromotionType.Pay) &&
        canShowPromotion(AddonPromotionType.Pay);

    const showCapturePromotion =
        showPromotions &&
        !showPayPromotion &&
        company &&
        isPayOrCapturePromotionsAllowedV2(company, AddonPromotionType.Capture) &&
        canShowPromotion(AddonPromotionType.Capture);

    const showFeedbackPromotion = showPromotions && !showPayPromotion && !showCapturePromotion;

    const showSettingsSectionForSimpleUser = isSimpleUser && isPaymentDetailsViewEnabled;

    const isPayMenuAvailable = company?.betaFeatures.includes(domain.CompanyBetaFeature.WalletsUK);

    const handleMenuClick = useHandleMenuClick(company, me);

    const starterGuideCompanies = useAtomValue(starterGuideCompaniesState);
    const isGettingStarted = useMemo(
        () => company?.id && starterGuideCompanies.includes(company.id),
        [company?.id, starterGuideCompanies]
    );

    if (!companies.length) return null;

    return (
        <DrawerUI
            notifications={<Invitations invitations={invitations?.items} />}
            notificationsCount={invitations?.items.length ?? 0}
            contentRef={contentRef}
        >
            {showPayPromotion && <DrawerPayAndCapturePromotion type={AddonPromotionType.Pay} />}

            {showCapturePromotion && <DrawerPayAndCapturePromotion type={AddonPromotionType.Capture} />}

            {showFeedbackPromotion && <FeedbackPromotion />}

            <AllCompaniesMenu onClick={handleMenuClick} />

            {isDrawerCollapsed ? (
                <Divider spacing='8 12' color='midnight50' />
            ) : (
                <CompanySelector
                    companies={companies}
                    canCreateNewCompanies={!!profile?.canCreateNewCompanies}
                    root={contentRef.current}
                />
            )}

            {company ? (
                <>
                    {!isDrawerCollapsed && (
                        <CompanyAlerts company={company} me={me} profileCreatedDate={profile?.createdDate} />
                    )}

                    <Grid gap={8}>
                        {isGettingStarted && <GettingStartedMenu companyId={company.id} onClick={handleMenuClick} />}

                        <AllRequestsMenu company={company} onClick={handleMenuClick} />
                    </Grid>

                    {!!workflows?.items.length && (
                        <>
                            <Divider spacing='8 12' color='midnight50' />

                            <WorkflowsMenu companyId={company.id} onClick={handleMenuClick} />
                        </>
                    )}

                    {isManager && isPayMenuAvailable && (
                        <>
                            <Divider spacing='8 12' color='midnight50' />

                            <PayMenu companyId={company.id} onClick={handleMenuClick} />
                        </>
                    )}

                    {isAuditorOrManager && (
                        <>
                            <Divider spacing='8 12' color='midnight50' />

                            <ReportsMenu companyId={company.id} onClick={handleMenuClick} />

                            <BudgetingMenu companyId={company.id} onClick={handleMenuClick} />

                            <SettingsMenu companyId={company.id} onClick={handleMenuClick} />
                        </>
                    )}

                    {(isWorkflowManager || showSettingsSectionForSimpleUser) && (
                        <>
                            <Divider spacing='8 12' color='midnight50' />

                            <SettingsMenu companyId={company.id} />
                        </>
                    )}

                    {!isDrawerCollapsed && (
                        <>
                            <Spacing height={16} />

                            <NavigationPromotion company={company} />
                        </>
                    )}
                </>
            ) : (
                <Loading />
            )}
        </DrawerUI>
    );
});

export default Drawer;
